import { OverflowList } from '@theme/critical';
import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    // Prefer JSON from simple card picker (cor+tamanho → variante combinada após morph)
    const simplePicker = this.querySelector('jv-simple-variant-picker-component');
    const variantScript = simplePicker?.querySelector('script[type="application/json"]');
    if (variantScript?.textContent) {
      try {
        const variant = JSON.parse(variantScript.textContent);
        if (variant?.id != null) return String(variant.id);
      } catch {
        // fall through
      }
    }

    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopColorCycle();
    this.removeEventListener('click', this.navigateToProduct);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Media id da cor selecionada (simple picker) ou da opção selecionada (swatches).
   * @returns {string | null}
   */
  #getSelectedColorMediaId() {
    const simplePicker = this.querySelector('jv-simple-variant-picker-component');
    if (simplePicker) {
      const colorInput = /** @type {HTMLInputElement | null} */ (
        simplePicker.querySelector(
          '.jv-simple-variant-picker__option--color input[type="radio"]:checked[data-option-media-id], input[name*="-swatch"]:checked[data-option-media-id]'
        )
      );
      return colorInput?.dataset?.optionMediaId || null;
    }

    return this.variantPicker?.selectedOption?.dataset?.optionMediaId || null;
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker) {
      return;
    }

    const selectedImageId = this.#getSelectedColorMediaId();

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return (
      this.querySelector('jv-simple-variant-picker-component') ||
      this.querySelector('swatches-variant-picker-component')
    );
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /** @type {ReturnType<typeof setInterval> | null} */
  #colorCycleTimer = null;

  /** @type {number} */
  #colorCycleIndex = 0;

  /**
   * Reads the media hover animation type from the card gallery.
   * Independent of the swatches block (tipo / hover vs clique).
   * @returns {'none'|'slide'|'fade'|'cycle_colors'}
   */
  get #mediaHoverAnimation() {
    const gallery = this.refs.cardGallery;
    const value = gallery?.dataset?.mediaHoverAnimation || 'slide';
    if (value === 'none' || value === 'fade' || value === 'cycle_colors') return value;
    return 'slide';
  }

  /**
   * Show a gallery slide by id. Uses slideshow.select when Scroller exists; always syncs
   * aria-hidden for media-stack / fade (select no-ops when slideshow inits with 1 visible slide).
   * @param {string} id
   * @param {{ animate?: boolean }} [options]
   */
  #showGallerySlide(id, options = {}) {
    const { slideshow, cardGallery } = this.refs;
    if (!slideshow || !id) return;

    const animate = Boolean(options.animate);
    const idStr = String(id);
    const allSlides = /** @type {HTMLElement[]} */ (
      slideshow.refs.slides?.length ? [...slideshow.refs.slides] : [...this.querySelectorAll('slideshow-slide')]
    );

    let target = allSlides.find((slide) => slide.getAttribute('slide-id') === idStr);
    if (!(target instanceof HTMLElement)) {
      const found = this.querySelector(`slideshow-slide[slide-id="${CSS.escape(idStr)}"]`);
      if (!(found instanceof HTMLElement)) return;
      target = found;
      allSlides.push(target);
    }

    target.hidden = false;
    target.removeAttribute('hidden');
    target.setAttribute('reveal', '');
    target.querySelector('img[loading="lazy"]')?.removeAttribute('loading');

    try {
      slideshow.select({ id: idStr }, undefined, { animate });
    } catch {
      // ignore
    }

    const stackPreview =
      cardGallery?.classList.contains('card-gallery--media-stack') ||
      cardGallery?.classList.contains('card-gallery--cycle-colors') ||
      this.#mediaHoverAnimation === 'cycle_colors' ||
      this.#mediaHoverAnimation === 'fade' ||
      slideshow.dataset.transitionStyle === 'fade';

    // Guarantee visibility when select no-ops (no Scroller) or fade stack is active
    if (stackPreview || target.getAttribute('aria-hidden') !== 'false') {
      for (const slide of allSlides) {
        const match = slide.getAttribute('slide-id') === idStr;
        slide.setAttribute('aria-hidden', match ? 'false' : 'true');
        if (match) {
          slide.hidden = false;
          slide.removeAttribute('hidden');
          slide.classList.remove('slideshow-slide--fade-out');
        }
      }
    }
  }

  /**
   * Ciclo: imagem principal + 1ª mídia de cada cor (data-color-cycle-ids).
   * @returns {string[]}
   */
  #getColorCycleMediaIds() {
    const { slideshow, cardGallery } = this.refs;
    if (!slideshow?.slides?.length && !slideshow?.refs?.slides?.length) return [];

    /** @type {string[]} */
    const ids = [];
    const seen = new Set();

    /** @param {string | null | undefined} id */
    const pushId = (id) => {
      if (!id || seen.has(id)) return;
      seen.add(id);
      ids.push(id);
    };

    const mainId =
      slideshow.initialSlide?.getAttribute('slide-id') ||
      slideshow.refs.slides?.[0]?.getAttribute('slide-id') ||
      slideshow.slides?.[0]?.getAttribute('slide-id');
    pushId(mainId);

    const csv = cardGallery?.dataset?.colorCycleIds || '';
    if (csv) {
      for (const raw of csv.split(',')) {
        pushId(raw.trim());
      }
    }

    return ids;
  }

  /**
   * Sync aria-hidden for the legacy card-gallery--hover-fade path (segunda imagem).
   */
  #syncFadeAriaHidden() {
    const { slideshow } = this.refs;
    if (!slideshow || this.#mediaHoverAnimation !== 'fade' || !slideshow.slides?.length) return;
    const current = slideshow.current;
    slideshow.slides.forEach((slide, i) => {
      slide.setAttribute('aria-hidden', i === current ? 'false' : 'true');
    });
  }

  #stopColorCycle() {
    if (this.#colorCycleTimer != null) {
      clearInterval(this.#colorCycleTimer);
      this.#colorCycleTimer = null;
    }
    this.refs.cardGallery?.classList.remove('is-color-cycling');
  }

  /**
   * @param {string[]} ids
   */
  #prepareColorCycleSlides(ids) {
    for (const id of ids) {
      const slide = this.querySelector(`slideshow-slide[slide-id="${CSS.escape(String(id))}"]`);
      if (!(slide instanceof HTMLElement)) continue;
      slide.hidden = false;
      slide.removeAttribute('hidden');
      slide.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
    }
  }

  #startColorCycle() {
    this.#stopColorCycle();

    const { slideshow, cardGallery } = this.refs;
    if (!slideshow || !isDesktopBreakpoint()) return;

    slideshow.dataset.transitionStyle = 'fade';
    cardGallery?.classList.add('is-color-cycling');

    const ids = this.#getColorCycleMediaIds();
    if (ids.length < 2) return;

    this.#prepareColorCycleSlides(ids);
    this.#showGallerySlide(ids[0], { animate: false });
    this.#colorCycleIndex = 0;

    this.#colorCycleTimer = setInterval(() => {
      this.#colorCycleIndex = (this.#colorCycleIndex + 1) % ids.length;
      const id = ids[this.#colorCycleIndex];
      if (!id) return;
      this.#showGallerySlide(id, { animate: true });
    }, 1000);
  }

  /**
   * Swatch preview (block setting hover/click) — independent of card "Ciclar cores".
   * @param {string} id - The media id to preview.
   */
  previewVariant(id) {
    if (!id) return;

    const { slideshow } = this.refs;
    if (!slideshow) return;

    this.resetVariant.cancel();
    this.#stopColorCycle();

    const useFade =
      this.#mediaHoverAnimation === 'fade' ||
      this.#mediaHoverAnimation === 'cycle_colors' ||
      slideshow.dataset.transitionStyle === 'fade' ||
      this.refs.cardGallery?.classList.contains('card-gallery--media-stack');

    this.#showGallerySlide(id, { animate: useFade });
    if (this.#mediaHoverAnimation === 'fade') {
      this.#syncFadeAriaHidden();
    }
  }

  /**
   * Card gallery hover — slide / fade / cycle_colors (theme setting).
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (!isDesktopBreakpoint()) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#mediaHoverAnimation === 'cycle_colors') {
      this.#startColorCycle();
      return;
    }

    if (this.#mediaHoverAnimation === 'none') return;

    const animate = this.#mediaHoverAnimation === 'slide';

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate });
    } else {
      slideshow.next(undefined, { animate });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
    if (this.#mediaHoverAnimation === 'fade') {
      this.#syncFadeAriaHidden();
    }
  }

  /**
   * Resets the image after card hover ends.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    this.#stopColorCycle();

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;

      if (this.#mediaHoverAnimation === 'cycle_colors') {
        const initialSlide = slideshow.initialSlide;
        const slideId = initialSlide?.getAttribute('slide-id');
        if (slideId) {
          this.#showGallerySlide(slideId, { animate: true });
          return;
        }
      }

      const animate = this.#mediaHoverAnimation === 'slide';
      slideshow.previous(undefined, { animate });
      if (this.#mediaHoverAnimation === 'fade') {
        this.#syncFadeAriaHidden();
      }
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the selected variant / initial slide.
   */
  #resetVariant = () => {
    this.#stopColorCycle();

    const { slideshow } = this.refs;

    if (!slideshow) return;

    const useFade =
      this.#mediaHoverAnimation === 'fade' ||
      this.#mediaHoverAnimation === 'cycle_colors' ||
      slideshow.dataset.transitionStyle === 'fade' ||
      this.refs.cardGallery?.classList.contains('card-gallery--media-stack');

    if (this.variantPicker?.selectedOption) {
      const id = this.#getSelectedColorMediaId() || this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        this.#showGallerySlide(id, { animate: useFade });
        if (this.#mediaHoverAnimation === 'fade') {
          this.#syncFadeAriaHidden();
        }
        return;
      }
    }

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideId) {
      this.#showGallerySlide(slideId, { animate: useFade });
      if (this.#mediaHoverAnimation === 'fade') {
        this.#syncFadeAriaHidden();
      }
      return;
    }

    const animate = this.#mediaHoverAnimation === 'slide';
    slideshow.previous(undefined, { animate });
    if (this.#mediaHoverAnimation === 'fade') {
      this.#syncFadeAriaHidden();
    }
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   */
  #handleCardVariantUrlUpdate() {
    if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', this.pendingVariantId);
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Snapshot visual swatch styles before morph (section re-render can drop HEX/image).
   * @returns {{id: string, style: string, className: string}[]}
   */
  #snapshotSwatchStyles() {
    /** @type {{id: string, style: string, className: string}[]} */
    const snapshots = [];

    for (const input of this.querySelectorAll('input[data-option-value-id]')) {
      if (!(input instanceof HTMLInputElement) || !input.dataset.optionValueId) continue;
      const swatch = input.parentElement?.querySelector('.swatch');
      if (!(swatch instanceof HTMLElement)) continue;

      snapshots.push({
        id: input.dataset.optionValueId,
        style: swatch.getAttribute('style') || '',
        className: swatch.className,
      });
    }

    return snapshots;
  }

  /**
   * Restore swatch backgrounds after morph.
   * @param {{id: string, style: string, className: string}[]} snapshots
   */
  #restoreSwatchStyles(snapshots) {
    for (const snap of snapshots) {
      const input = this.querySelector(`input[data-option-value-id="${snap.id}"]`);
      if (!(input instanceof HTMLInputElement)) continue;
      const swatch = input.parentElement?.querySelector('.swatch');
      if (!(swatch instanceof HTMLElement)) continue;

      if (snap.style) swatch.setAttribute('style', snap.style);
      if (snap.className) swatch.className = snap.className;
    }
  }

  /**
   * In image mode, fill swatches from card gallery slides when media id is known.
   */
  #applyImageSwatchesFromCard() {
    if (this.dataset.swatchDisplayType !== 'image') return;

    const card = this.parentProductCard;
    if (!(card instanceof ProductCard)) return;

    for (const input of this.querySelectorAll('input[data-option-media-id]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const mediaId = input.dataset.optionMediaId;
      if (!mediaId) continue;

      const slide = card.querySelector(`slideshow-slide[slide-id="${mediaId}"]`);
      const img = slide?.querySelector('img');
      const src = img instanceof HTMLImageElement ? img.currentSrc || img.src : '';
      const swatch = input.parentElement?.querySelector('.swatch');
      if (!src || !(swatch instanceof HTMLElement)) continue;

      swatch.style.setProperty('--swatch-background', `url(${src})`);
      swatch.classList.add('swatch--image');
      swatch.classList.remove('swatch--unavailable');
    }
  }

  /**
   * Preserve custom Cor/HEX/image swatches across section-rendering morph.
   * @param {Document} newHtml
   */
  updateVariantPicker(newHtml) {
    const savedDisplayType = this.dataset.swatchDisplayType;
    const savedMediaTrigger = this.dataset.swatchMediaTrigger;
    const snapshots = this.#snapshotSwatchStyles();
    const newSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (savedDisplayType && newSource instanceof HTMLElement) {
      newSource.dataset.swatchDisplayType = savedDisplayType;
    }
    if (savedMediaTrigger && newSource instanceof HTMLElement) {
      newSource.dataset.swatchMediaTrigger = savedMediaTrigger;
    }

    const newProduct = VariantPicker.prototype.updateVariantPicker.call(this, newHtml);

    if (savedDisplayType) {
      this.dataset.swatchDisplayType = savedDisplayType;
    }
    if (savedMediaTrigger) {
      this.dataset.swatchMediaTrigger = savedMediaTrigger;
    }

    this.#restoreSwatchStyles(snapshots);
    this.#applyImageSwatchesFromCard();

    return newProduct;
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

/**
 * Seletor simplificado (Cor imagem + Tamanho botão) para cards Compre Junto.
 * Estende VariantPicker (não Swatches) para preservar option_values de cor+tamanho.
 * @extends {VariantPicker}
 */
class JvSimpleVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();
    this.parentProductCard = this.closest('product-card');
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
    // Hover via JS (não Liquid on:) — sobrevive ao morph do section-rendering
    this.addEventListener('pointerenter', this.#onColorPointerEnter, true);
    this.addEventListener('pointerleave', this.#onColorPointerLeave, true);
  }

  disconnectedCallback() {
    this.removeEventListener('pointerenter', this.#onColorPointerEnter, true);
    this.removeEventListener('pointerleave', this.#onColorPointerLeave, true);
    super.disconnectedCallback?.();
  }

  /**
   * @param {PointerEvent} event
   */
  #onColorPointerEnter = (event) => {
    if (this.dataset.swatchMediaTrigger !== 'hover') return;
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('.jv-simple-variant-picker__label--color');
    if (!label || !this.contains(label)) return;

    const mediaId =
      label instanceof HTMLElement
        ? label.dataset.mediaId || label.querySelector('input')?.dataset?.optionMediaId
        : null;
    if (mediaId && this.parentProductCard instanceof ProductCard) {
      this.parentProductCard.previewVariant(mediaId);
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #onColorPointerLeave = (event) => {
    if (this.dataset.swatchMediaTrigger !== 'hover') return;
    if (!(event.target instanceof Element)) return;

    const label = event.target.closest('.jv-simple-variant-picker__label--color');
    if (!label || !this.contains(label)) return;

    const related = event.relatedTarget;
    if (related instanceof Node && label.contains(related)) return;

    if (this.parentProductCard instanceof ProductCard) {
      this.parentProductCard.resetVariant();
    }
  };

  /**
   * @param {VariantUpdateEvent} [event]
   */
  #handleCardVariantUrlUpdate(event) {
    const variantId = event?.detail?.resource?.id ?? this.pendingVariantId;
    if (variantId && this.parentProductCard instanceof ProductCard) {
      const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
      currentUrl.searchParams.set('variant', String(variantId));
      this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
      this.pendingVariantId = null;
    }
  }

  /**
   * Preserva mídia/estilo das cores: o morph com tamanho muda featured_media da opção.
   * @returns {{id: string, mediaId: string, style: string, className: string}[]}
   */
  #snapshotColorSwatches() {
    /** @type {{id: string, mediaId: string, style: string, className: string}[]} */
    const snapshots = [];

    for (const input of this.querySelectorAll(
      '.jv-simple-variant-picker__option--color input[data-option-value-id], input[name*="-swatch"][data-option-value-id]'
    )) {
      if (!(input instanceof HTMLInputElement) || !input.dataset.optionValueId) continue;
      const swatch = input.parentElement?.querySelector('.swatch');
      snapshots.push({
        id: input.dataset.optionValueId,
        mediaId: input.dataset.optionMediaId || '',
        style: swatch instanceof HTMLElement ? swatch.getAttribute('style') || '' : '',
        className: swatch instanceof HTMLElement ? swatch.className : '',
      });
    }

    return snapshots;
  }

  /**
   * @param {{id: string, mediaId: string, style: string, className: string}[]} snapshots
   */
  #restoreColorSwatches(snapshots) {
    for (const snap of snapshots) {
      const input = this.querySelector(`input[data-option-value-id="${snap.id}"]`);
      if (!(input instanceof HTMLInputElement)) continue;

      if (snap.mediaId) {
        input.dataset.optionMediaId = snap.mediaId;
        const label = input.closest('label');
        if (label instanceof HTMLElement) {
          label.dataset.mediaId = snap.mediaId;
        }
      }

      const swatch = input.parentElement?.querySelector('.swatch');
      if (!(swatch instanceof HTMLElement)) continue;
      if (snap.style) swatch.setAttribute('style', snap.style);
      if (snap.className) swatch.className = snap.className;
    }
  }

  /**
   * Preenche swatches imagem a partir dos slides do card (ids estáveis).
   */
  #applyImageSwatchesFromCard() {
    if (this.dataset.swatchDisplayType !== 'image') return;

    const card = this.parentProductCard;
    if (!(card instanceof ProductCard)) return;

    for (const input of this.querySelectorAll('input[data-option-media-id]')) {
      if (!(input instanceof HTMLInputElement)) continue;
      const mediaId = input.dataset.optionMediaId;
      if (!mediaId) continue;

      const slide = card.querySelector(`slideshow-slide[slide-id="${mediaId}"]`);
      const img = slide?.querySelector('img');
      const src = img instanceof HTMLImageElement ? img.currentSrc || img.src : '';
      const swatch = input.parentElement?.querySelector('.swatch');
      if (!src || !(swatch instanceof HTMLElement)) continue;

      swatch.style.setProperty('--swatch-background', `url(${src})`);
      swatch.classList.add('swatch--image');
      swatch.classList.remove('swatch--unavailable');
    }
  }

  /**
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const mediaId = event.target.dataset?.optionMediaId;
    if (mediaId && this.parentProductCard instanceof ProductCard) {
      this.parentProductCard.previewVariant(mediaId);
    }

    const variantId = event.target.dataset?.variantId || event.target.dataset?.firstAvailableOrFirstVariantId;
    if (variantId) {
      this.pendingVariantId = variantId;
    }

    super.variantChanged(event);
  }

  /**
   * @param {Document} newHtml
   */
  updateVariantPicker(newHtml) {
    const savedDisplayType = this.dataset.swatchDisplayType;
    const savedMediaTrigger = this.dataset.swatchMediaTrigger;
    const colorSnapshots = this.#snapshotColorSwatches();
    const newSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (savedDisplayType && newSource instanceof HTMLElement) {
      newSource.dataset.swatchDisplayType = savedDisplayType;
    }
    if (savedMediaTrigger && newSource instanceof HTMLElement) {
      newSource.dataset.swatchMediaTrigger = savedMediaTrigger;
    }

    const newProduct = VariantPicker.prototype.updateVariantPicker.call(this, newHtml);

    if (savedDisplayType) {
      this.dataset.swatchDisplayType = savedDisplayType;
    }
    if (savedMediaTrigger) {
      this.dataset.swatchMediaTrigger = savedMediaTrigger;
    }

    this.#restoreColorSwatches(colorSnapshots);
    this.#applyImageSwatchesFromCard();

    // Mantém galeria na cor selecionada (não na featured_media do tamanho)
    const checkedColor = /** @type {HTMLInputElement | null} */ (
      this.querySelector(
        '.jv-simple-variant-picker__option--color input:checked[data-option-media-id], input[name*="-swatch"]:checked[data-option-media-id]'
      )
    );
    if (checkedColor?.dataset?.optionMediaId && this.parentProductCard instanceof ProductCard) {
      this.parentProductCard.previewVariant(checkedColor.dataset.optionMediaId);
    }

    return newProduct;
  }
}

if (!customElements.get('jv-simple-variant-picker-component')) {
  customElements.define('jv-simple-variant-picker-component', JvSimpleVariantPickerComponent);
}
